<template>
	<div id="app">
		<template v-if="authStatus === UserStatus.Unknown">
			<fullscreen-loading-indicator></fullscreen-loading-indicator>
		</template>
		<template v-else>
			<router-view />
		</template>
		<div class="notification-section">
			<div
				class="notification"
				v-for="(message, i) in messages"
				:key="'notif' + i"
				:class="{
					'is-danger': message.level === Level.DANGER,
					'is-warning': message.level === Level.WARNING,
					'is-bg-primary-alt': message.level === Level.INFO,
				}"
			>
				<button
					class="delete"
					@click="deleteSnackbarAt(message.index)"
				></button>
				{{ message.message }}
			</div>
		</div>
		<div class="update-sw-banner is-bg-primary py-2" v-if="showUpdateSWBanner">
			<span class="px-4">Nuevo contenido disponible</span>
			<button class="button is-bg-white is-txt-black is-small"
				@click="updateServiceWorkerConfirmed">Actualizar</button>
		</div>
	</div>
</template>

<script lang="ts">
	import { defineComponent, provide } from "vue";
	import { UserStatus } from "@/infraestructure/auth/auth_facade";
	import { PatientRepository } from "@/infraestructure/patient/PatientRepository";
	import useNotification from "@/store/NotificationState";
	import useAuth from "@/store/AuthState";
	import { combineLatest } from "rxjs";
	import { pprint } from "@/helpers/pprint";
	import { useRouter } from "vue-router";
	import { DoctorRepository } from "./infraestructure/doctor/DoctorRepository";
	import FullscreenLoadingIndicator from "@/components/FullscreenLoadingIndicator.vue";
import useApp from "./store/AppState";

	export default defineComponent({
		components: {
			FullscreenLoadingIndicator,
		},
		setup() {
			const { init, authStatus, statusListener, updateFCMToken } = useAuth();
			init();
			provide(PatientRepository.key, new PatientRepository());
			provide(DoctorRepository.key, new DoctorRepository());

			const router = useRouter();
			router.beforeEach((to, from, next) => {
				if (to.name !== "Login" && authStatus.value === UserStatus.SignedOut)
					next({ name: "Login" });
				else if (to.name === "Login" && authStatus.value === UserStatus.SignedIn)
					next({ name: "dashboard" });
				else next();
			});

			const { showUpdateSWBanner, updateServiceWorkerConfirmed } = useApp();
			const {
				messages,
				deleteSnackbarAt,
				// listenTokenRefresh,
				fcmTokenListener,
				listenOnMessage,
				SnackbarMessageLevel,
			} = useNotification();
			listenOnMessage();

			combineLatest(statusListener, fcmTokenListener).subscribe((r) => {
				const isSignedIn = authStatus.value === UserStatus.SignedIn;
				const fcmToken = r[1];
				pprint("-- combined listener --", r);
				if (isSignedIn && fcmToken !== null) {
					pprint("-- update FCM triggered --");
					updateFCMToken(fcmToken);
				}
			});
			// listenTokenRefresh();

			return {
				authStatus,
				UserStatus,
				messages,
				deleteSnackbarAt,
				Level: SnackbarMessageLevel,
				showUpdateSWBanner,
				updateServiceWorkerConfirmed,
			};
		},
	});
</script>

<style lang="stylus">
	@import './styles/elements.styl';
	@import './styles/colors.styl';
	@import './styles/fonts.styl';

	#app {
		font-family: primaryFont;
		-webkit-font-smoothing: antialiased;
		-moz-osx-font-smoothing: grayscale;
		text-align: center;
	}

	.notification-section {
		position: fixed;
		bottom: 1rem;
		left: 0.5em;
		z-index: 99;
	}

	.update-sw-banner {
		position: fixed;
		bottom: 0;
		left: 0;
		width: 100%;
		button {
			vertical-align: middle;
		}
	}
</style>
